import {DeviceType} from '@gongxigames/framework/core';
import {DisplayObject} from 'pixi.js';

export type ScaleConfigProperty = {
    scale?: number;
    yOffset?: number;
    xOffset?: number;
};

export type MobileScaleConfigParameters = {
    landscape?: ScaleConfigProperty;
    portrait?: ScaleConfigProperty;
};

export type ScaleConfig = {
    mobile?: MobileScaleConfigParameters;
    desktop?: ScaleConfigProperty;
};

export type ScaleParameters = {
    x: number;
    y: number;
    scale: number;
};

export type PlatformScaleParameters = {
    portrait: ScaleParameters;
    landscape: ScaleParameters;
    desktop: ScaleParameters;
};

export class ScaleUtils {
    protected scaleParamsByConfigName: Record<string, PlatformScaleParameters> = {};

    constructor(configs: Record<string, ScaleConfig>, protected deviceScale: number, protected viewportWidth: number) {
        for (const configName in configs) {
            if (!configs.hasOwnProperty(configName)) {
                continue;
            }

            this.scaleParamsByConfigName[configName] = this.getScaleParameters(
                configs[configName],
                deviceScale,
                viewportWidth
            );
        }
    }

    public applyNewScaleConfig(configName: string, config: ScaleConfig): void {
        this.scaleParamsByConfigName[configName] = this.getScaleParameters(
            config,
            this.deviceScale,
            this.viewportWidth,
            this.scaleParamsByConfigName[configName]
        );
    }

    public scaleDisplayObject(configName: string, target: keyof PlatformScaleParameters, ...displayObjects: DisplayObject[]): void {
        displayObjects.forEach(displayObject => {
            this.applyScaleToDisplayObject(this.scaleParamsByConfigName[configName][target], displayObject);
        });
    }

    public scaleByTargetPlatform(configName: string, platform: DeviceType, isLandscapeOrientation: boolean, ...containers: DisplayObject[]): void {
        let target: keyof PlatformScaleParameters = 'desktop';

        if (platform) {
            if (platform === DeviceType.MOBILE) {
                target = isLandscapeOrientation ? 'landscape' : 'portrait';
            } else {
                target = 'desktop';
            }
        }

        this.scaleDisplayObject(configName, target, ...containers);
    }

    public getAllScaleParameters(configName: string): PlatformScaleParameters {
        return this.scaleParamsByConfigName[configName];
    }

    public mobileLandscapeScale(configName: string, ...displayObjects: DisplayObject[]): void {
        this.scaleDisplayObject(configName,'landscape', ...displayObjects);
    }

    public mobilePortraitScale(configName: string, ...displayObjects: DisplayObject[]): void {
        this.scaleDisplayObject(configName,'portrait', ...displayObjects);
    }

    public desktopScale(configName: string, ...displayObjects: DisplayObject[]): void {
        this.scaleDisplayObject(configName,'desktop', ...displayObjects);
    }

    protected applyScaleToDisplayObject(parameters: ScaleParameters, displayObject: DisplayObject): void {
        if (displayObject) {
            displayObject.scale.set(parameters.scale);
            displayObject.x = parameters.x;
            displayObject.y = parameters.y;
        }
    }

    protected getScaleParameters(
        config: ScaleConfig,
        deviceScale: number,
        viewportWidth: number,
        oldScaleParameters: PlatformScaleParameters = {
            portrait: {x: 0, y: 0, scale: 1},
            landscape: {x: 0, y: 0, scale: 1},
            desktop: {x: 0, y: 0, scale: 1}
        }
    ): PlatformScaleParameters {
        const {mobile, desktop} = config;
        const scaleParams: PlatformScaleParameters = oldScaleParameters;

        if (mobile) {
            const {landscape, portrait} = mobile;

            if (portrait) {
                if (portrait.scale !== undefined) {
                    scaleParams.portrait.x = (viewportWidth * deviceScale - viewportWidth * portrait.scale) / 2;
                    scaleParams.portrait.scale = portrait.scale;
                }
                scaleParams.portrait.y = portrait.yOffset ?? scaleParams.portrait.y;
                scaleParams.portrait.x += portrait.xOffset ?? 0;
            }

            if (landscape) {
                scaleParams.landscape.y = landscape.yOffset ?? scaleParams.landscape.y;
                scaleParams.landscape.x = landscape.xOffset ?? 0;
                scaleParams.landscape.scale = landscape.scale ?? scaleParams.landscape.scale;
            }
        }

        if (desktop) {
            scaleParams.desktop.scale = desktop.scale ?? scaleParams.desktop.scale;
            scaleParams.desktop.x = desktop.xOffset ?? 0;
            scaleParams.desktop.y = desktop.yOffset ?? 0;
        }

        return scaleParams;
    }
}
